import React, { useLayoutEffect, useRef, useState } from "react";
import { TABLE_TOP_CORNERS, useGridHeaderCorners } from "../../v2/components/tableHeaderCorners.js";
import { T } from "../../app/tokens.js";
import { STATUS_LABEL, LONG_RANGE_NOTE, commitmentsForPeriod, commitmentLabel, monthLabel, rowSummary, disclosureLines } from "./capacityModel.js";
import { INK, ROW_HEIGHT, CHANGED_OUTLINE, LONG_RANGE_OPACITY, statusPillStyle } from "./capacityStyles.js";
import { GRID_CELL, GRID_GAP, GRID_RADIUS, GRID_SIZE, GRID_OPEN_BG } from "./capacityGridStyles.js";
import "./capacityGrid.css";
import { SeatGrid } from "./SeatGrid.jsx";
import { PartialMarker } from "../../components/PartialTotal.jsx";
import { coverageText } from "../../services/seatAccounting.js";

const NOW_W = 124;
const ISSUE_W = 210;
// Name column sized to the longest label so full names never truncate.
export function nameColumnWidth(rows) {
  const longest = Math.max(10, ...rows.map((r) => (r.market.name ?? "").length));
  return Math.min(170, Math.max(140, Math.ceil(longest * 7.4) + 34));
}
const seatText = (e) => `${e.demandBasis === "assigned_today" ? "Assigned today" : "Forecast demand"} ${round(e.demand)} · Usable seats ${round(e.seats)} (${round(e.installedSeats)} installed seats)${e.max != null ? ` · max ≈ ${round(e.max)} usable seats (${round(e.recordedMax)} desks)` : ""}`;
const round = (n) => (n == null ? "\u2013" : Math.round(n).toLocaleString());

function tooltipLines(row, pi, period, months, view, scenarioOn) {
  const b = row.baseline[pi];
  const s = row.scenario?.[pi];
  const shown = scenarioOn ? s : b;
  const lines = [];
  const q = view === "quarterly";
  const fm = (e) => monthLabel(months[e?.monthIndex]?.start);
  if (scenarioOn) {
    if (q) lines.push(s.monthIndex === b.monthIndex ? `Fullest month: ${fm(s)}` : `Fullest month: scenario ${fm(s)}, today ${fm(b)}`);
    lines.push(`Scenario: ${seatText(s)} · ${STATUS_LABEL[s.status]}`);
    lines.push(`Today: ${seatText(b)} · ${STATUS_LABEL[b.status]}`);
  } else {
    if (q && shown) lines.push(`Fullest month: ${fm(b)}`);
    lines.push(`${seatText(b)} · ${STATUS_LABEL[b.status]}`);
  }
  for (const l of disclosureLines(shown)) lines.push(l);
  for (const c of commitmentsForPeriod(row.market, period, months)) lines.push(`\u25C6 ${commitmentLabel(c)}`);
  if (shown?.longRange) lines.push("Long-range projection, lower confidence");
  return lines;
}

function IssuePill({ summary, periods }) {
  const fi = summary.firstIssue;
  if (!fi) return <span style={{ fontSize: T.fsXxs, color: T.light }}>None in view</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
      <span data-testid="capacity-issue-pill" data-status={fi.status} style={statusPillStyle(fi.status)}>{STATUS_LABEL[fi.status]}</span>
      <span style={{ fontSize: T.fsXxs, color: T.dark, fontWeight: T.fwSemibold }}>{periods[fi.idx]?.full}</span>
    </span>
  );
}

export function CapacityGrid({ rows, periods, months, view, focusIdx, onFocus, openIds, onToggleOpen, scenarioOn, nameWidth, compact = false }) {
  const [tip, setTip] = useState(null);
  const viewportRef = useRef(null);
  const headerRef = useRef(null);
  useGridHeaderCorners(headerRef, viewportRef);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const node = viewportRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => setWidth(node.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const N = periods.length;
  const quarterly = view === "quarterly";
  const gap = GRID_GAP;
  const squareSize = compact ? GRID_SIZE.compact : GRID_SIZE.standard;
  const inlineSummary = width >= 1180;
  const summaryWidth = inlineSummary ? NOW_W + ISSUE_W + gap * 2 : 0;
  const size = squareSize;
  const cols = `${nameWidth}px repeat(${N}, ${size}px)${inlineSummary ? ` ${NOW_W}px ${ISSUE_W}px` : ""}`;
  const gridWidth = nameWidth + N * (size + gap) + summaryWidth;
  const years = periods.reduce((groups, p, i) => {
    const year = p.key.slice(0, 4);
    const last = groups.at(-1);
    if (last?.year === year) last.count += 1;
    else groups.push({ year, start: i, count: 1 });
    return groups;
  }, []);
  const headStyle = { fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em", color: T.light, whiteSpace: "nowrap" };
  const anyLong = periods.some((p, i) => rows.some((r) => (scenarioOn ? r.scenario : r.baseline)[i]?.longRange));
  const focus = periods[focusIdx];

  const showTip = (e, ri, pi) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTip({ ri, pi, x: rect.left + rect.width / 2, y: rect.bottom + 6 });
  };

  return (
    <div ref={viewportRef} data-testid="capacity-grid-scroll" style={{ ...TABLE_TOP_CORNERS, overflowX: "auto", overflowY: "visible", minWidth: 0, position: "relative", containerType: "inline-size",
      "--capacity-focus": T.blue, "--capacity-row-bg": T.card, "--capacity-hover-bg": T.hoverBg,
      "--capacity-open-bg": GRID_OPEN_BG, "--capacity-chevron-rest": T.light, "--capacity-chevron-hover": T.mid,
      "--capacity-chevron-bg": T.lightGray, "--capacity-chevron-open": T.blue }}>
      <div role="grid" aria-label="Capacity by market and period" data-testid="capacity-grid" data-cell-size={size} style={{ ...TABLE_TOP_CORNERS, width: gridWidth, minWidth: "100%", display: "flex", flexDirection: "column" }}>
        <div aria-hidden="true" data-testid="capacity-year-header" style={{ display: "grid", gridTemplateColumns: cols, columnGap: gap, height: 18 }}>
          {years.map((group) => <span key={group.year} data-capacity-year={group.year} style={{ gridColumn: `${group.start + 2} / span ${group.count}`, fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwBold, whiteSpace: "nowrap" }}>{group.count * (size + gap) < 26 ? `'${group.year.slice(2)}` : group.year}</span>)}
        </div>
        <div ref={headerRef} role="row" style={{ ...TABLE_TOP_CORNERS, display: "grid", gridTemplateColumns: cols, columnGap: gap, alignItems: "end", height: quarterly ? 44 : 26, position: "sticky", top: 0, background: T.card, zIndex: 2 }}>
          <div style={{ position: "sticky", left: 0, background: T.card, zIndex: 3, ...headStyle, paddingLeft: 6 }}>Market</div>
          {periods.map((p, i) => (
            <button key={p.key} type="button" role="columnheader" className="capacity-period-axis" data-testid={`capacity-period-${p.key}`}
              aria-pressed={i === focusIdx} aria-label={`${i === 0 ? `Now, ${monthLabel(months[0]?.start)}; ` : ""}Focus ${p.full}`} title={p.full}
              onClick={() => onFocus(i)}
              style={{
                border: 0, padding: "2px 0", borderRadius: GRID_RADIUS, width: size, overflow: "visible", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", textAlign: "center",
                fontSize: T.fsXxs, fontWeight: i === focusIdx || p.yearStart ? T.fwBold : T.fwSemibold,
                color: p.yearStart ? T.dark : T.mid, background: "transparent",
              }}>{i === 0 && <span data-testid="capacity-axis-now" aria-hidden="true" style={{ display: quarterly ? "block" : "inline-block", margin: "0 auto", width: 14, borderRadius: 4, background: INK, color: T.card }}>N</span>}
                {(quarterly || i !== 0) && <span>{quarterly ? p.key.slice(5) : p.label.startsWith("'") ? "J" : p.label}</span>}</button>
          ))}
           {inlineSummary && <><div data-testid="capacity-head-now" style={headStyle}>Now</div>
           <div data-testid="capacity-head-issue" style={headStyle}>First issue</div></>}
        </div>

        {rows.length === 0 && (
          <div data-testid="capacity-empty" style={{ padding: "28px 8px", fontSize: T.fsSm, color: T.mid }}>
            No markets match these filters. Clear search or filters to see the eligible active markets.
          </div>
        )}

        {rows.map((row, ri) => {
          const m = row.market;
          const isOpen = openIds.includes(m.id);
          const cells = scenarioOn ? row.scenario : row.baseline;
          const summary = rowSummary(row, scenarioOn);
          const nowCell = row.current?.[scenarioOn ? "scenario" : "baseline"];
          return (
            <React.Fragment key={m.id}>
              <div role="row" className="capacity-market-row" data-open={isOpen} data-testid={`capacity-row-${m.id}`}
                onClick={() => { setTip(null); onToggleOpen(m.id); }}
                style={{ display: "grid", gridTemplateColumns: cols, columnGap: gap, alignItems: "center", minHeight: compact ? ROW_HEIGHT.compact : ROW_HEIGHT.default, paddingTop: 9, paddingBottom: 4, boxSizing: "border-box", borderTop: `1px solid ${T.hair}` }}>
                <button type="button" className="capacity-market-name" aria-label={`${m.name} capacity details`} aria-expanded={isOpen} aria-controls={`capacity-detail-${m.id}`} data-testid={`capacity-toggle-${m.id}`}
                  style={{ position: "sticky", left: 0, zIndex: 1, border: 0, textAlign: "left", cursor: "pointer", fontFamily: "inherit", padding: "0 6px", height: "100%", minWidth: 0, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                  <span style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <span data-testid={`capacity-name-${m.id}`} style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark, overflowWrap: "anywhere", lineHeight: 1.2 }}>{m.name}</span>
                    <span data-testid={`capacity-subtitle-${m.id}`} style={{ fontSize: T.fsXxs, lineHeight: 1.1, color: T.mid }}
                      title={`Demand = assigned today for ${monthLabel(months[0]?.start)}; usable seats = installed seats adjusted for attendance (policy)`}
                      aria-label={`Demand ${round(m.totalPeople)} · Usable seats ${round(m.totalUsableSeats)}. Demand = assigned today for ${monthLabel(months[0]?.start)}; usable seats = installed seats adjusted for attendance (policy). ${coverageText(m.months?.[0]?.demandCoverage, "demand")}`}>
                      Demand {round(m.totalPeople)} · Usable seats {round(m.totalUsableSeats)} <span aria-hidden="true">ⓘ</span>
                    </span>
                    <PartialMarker coverage={m.months?.[0]?.demandCoverage} measure="demand" />
                  </span>
                  <span className="capacity-row-chevron" data-testid={`capacity-chevron-${m.id}`} aria-hidden="true" style={{ width: 22, height: 22, flexShrink: 0, borderRadius: 6, display: "inline-flex", justifyContent: "center", alignItems: "center", fontSize: T.fsLg }}>›</span>
                </button>
                {periods.map((p, pi) => {
                  const c = cells[pi];
                  const st = GRID_CELL[c?.status] ?? GRID_CELL.no_data;
                  const changed = scenarioOn && row.scenario[pi]?.status !== row.baseline[pi]?.status;
                  const marks = commitmentsForPeriod(m, p, months);
                  return (
                    <button key={p.key} type="button" role="gridcell" className="capacity-heatmap-cell"
                      data-testid={`capacity-cell-${m.id}-${p.key}`} data-status={c?.status} data-changed={changed ? "true" : undefined}
                      data-long-range={c?.longRange ? "true" : undefined}
                      aria-label={`${m.name}, ${p.full}: ${tooltipLines(row, pi, p, months, view, scenarioOn).join("; ")}`}
                      onMouseEnter={(e) => showTip(e, ri, pi)} onFocus={(e) => showTip(e, ri, pi)}
                      onMouseLeave={() => setTip(null)} onBlur={() => setTip(null)}
                      onClick={(e) => { e.stopPropagation(); setTip(null); onFocus(pi, m.id); }}
                      style={{
                        position: "relative", justifySelf: "center", width: squareSize, height: squareSize, flex: "none", padding: 0, cursor: "pointer", borderRadius: GRID_RADIUS, boxSizing: "border-box",
                        background: st.bg, border: ["no_data", "history"].includes(c?.status) ? `1px ${st.dashed ? "dashed" : "solid"} ${st.border}` : 0,
                        opacity: c?.longRange ? LONG_RANGE_OPACITY : 1,
                        outline: changed ? `${CHANGED_OUTLINE}px solid ${INK}` : "none",
                        outlineOffset: changed ? 0 : 1,
                      }}>
                      {marks.length > 0 && (
                        <span data-testid={`capacity-marker-${m.id}-${p.key}`} aria-hidden="true"
                          style={{ position: "absolute", top: -9, left: "50%", width: 5, height: 5, background: INK, transform: "translateX(-50%) rotate(45deg)" }} />
                      )}
                    </button>
                  );
                })}
                <div data-testid={`capacity-now-${m.id}`} title={months[0] ? `${monthLabel(months[0].start)}${nowCell?.installedSeats != null ? ` · ${round(nowCell.installedSeats)} installed` : ""}` : undefined}
                  style={{ ...(inlineSummary ? {} : { gridColumn: "2 / -1", gridRow: 2, paddingBottom: 5, width: "max-content" }), fontSize: T.fsXxs, fontVariantNumeric: "tabular-nums", color: T.dark, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                  {!inlineSummary && <span style={{ color: T.light }}>Now</span>}
                  {["excluded", "no_seats"].includes(summary.now?.status)
                    ? <span style={{ color: T.mid }}>{STATUS_LABEL[summary.now.status]}</span>
                    : <><b>{round(summary.now?.demand)}</b><span style={{ color: T.mid }}> / {round(summary.now?.seats)}</span></>}
                  {!inlineSummary && <span data-testid={`capacity-issue-${m.id}`} data-issue-status={summary.firstIssue?.status ?? "none"} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 8 }}><span style={{ color: T.light }}>First issue</span><IssuePill summary={summary} periods={periods} /></span>}
                </div>
                {inlineSummary && <div data-testid={`capacity-issue-${m.id}`} data-issue-status={summary.firstIssue?.status ?? "none"}>
                  <IssuePill summary={summary} periods={periods} />
                </div>}
              </div>
              {isOpen && focus && (
                <div id={`capacity-detail-${m.id}`} data-testid={`capacity-expanded-${m.id}`} style={{ position: "sticky", left: 0, width: "min(100%, 100cqw)", maxWidth: "100%", boxSizing: "border-box", padding: "10px 12px 14px 26px", background: GRID_OPEN_BG, borderRadius: "0 0 8px 8px", margin: "0 0 6px" }}>
                  <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 10 }}>
                    <b style={{ color: T.dark }}>{m.name}</b> · seats in <b style={{ color: T.dark }}>{focus.full}</b>
                    {view === "quarterly" && row.baseline[focusIdx] ? ` (fullest month: ${monthLabel(months[row.baseline[focusIdx].monthIndex]?.start)})` : ""}
                    {row.baseline[focusIdx]?.longRange ? " · long-range projection" : ""}
                  </div>
                  <div data-testid={`capacity-expanded-seats-${m.id}`} style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 8 }}>
                    {round(row.baseline[focusIdx]?.installedSeats)} installed seats · {round(row.baseline[focusIdx]?.seats)} usable seats
                    {row.baseline[focusIdx]?.max != null ? ` · max ≈ ${round(row.baseline[focusIdx].max)} usable seats (${round(row.baseline[focusIdx].recordedMax)} recorded)` : " · no recorded maximum headroom"}
                    {disclosureLines((scenarioOn ? row.scenario : row.baseline)[focusIdx]).map((l) => <div key={l} data-testid={`capacity-disclosure-${m.id}`}>{l}</div>)}
                  </div>
                  <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                    <SeatGrid testId={`capacity-seats-today-${m.id}`} title={row.baseline[focusIdx]?.demandBasis === "assigned_today" ? "Assigned today" : "Forecast"} evalResult={row.baseline[focusIdx]} />
                    {scenarioOn
                      ? <SeatGrid testId={`capacity-seats-scenario-${m.id}`} title="Scenario" evalResult={row.scenario[focusIdx]} />
                      : <div style={{ flex: 1, fontSize: T.fsXs, color: T.mid }}><b style={{ color: T.dark }}>Scenario</b><div style={{ marginTop: 6 }}>Open Scenario and change a control to compare.</div></div>}
                  </div>
                  <div data-testid={`capacity-commitments-${m.id}`} style={{ marginTop: 10, fontSize: T.fsXs, color: T.mid }}>
                    {(m.commitments ?? []).filter((c) => c.delta).length === 0
                      ? "No committed seat changes in the capacity schedule."
                      : (m.commitments ?? []).filter((c) => c.delta).map((c) => (
                        <div key={`${c.effectiveDate}-${c.projectId ?? c.label}-${c.delta}`} style={{ color: T.dark }}>{"\u25C6"} {monthLabel(c.effectiveDate)}: {commitmentLabel(c)}</div>
                      ))}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
        {anyLong && (
          <div data-testid="capacity-long-range-note" style={{ fontSize: T.fsXs, color: T.mid, padding: "8px 6px", position: "sticky", left: 0 }}>{LONG_RANGE_NOTE}</div>
        )}
      </div>
      {tip && rows[tip.ri] && (
        <div role="tooltip" data-testid="capacity-tooltip" style={{ position: "fixed", left: Math.max(12, Math.min(tip.x - 180, window.innerWidth - 372)), top: tip.y, width: 360, maxWidth: "calc(100vw - 24px)", boxSizing: "border-box", zIndex: 9000, pointerEvents: "none", background: INK, color: T.card, borderRadius: 8, padding: "8px 10px", fontSize: T.fsXs, lineHeight: 1.5, boxShadow: T.shadowFloat, whiteSpace: "normal", overflowWrap: "anywhere" }}>
          <b>{rows[tip.ri].market.name} · {periods[tip.pi].full}</b>
          {tooltipLines(rows[tip.ri], tip.pi, periods[tip.pi], months, view, scenarioOn).map((l) => <div key={l}>{l}</div>)}
        </div>
      )}
    </div>
  );
}
